/**
 * Dictare pentru chatul configuratorului: Web Speech API nativ (gratuit,
 * fără backend, fără chei). Merge doar în context securizat (HTTPS sau
 * localhost) și doar în browserele care îl au (Chrome/Edge/Safari) - unde
 * lipsește, butonul de microfon nu se afișează deloc.
 *
 * Orb-chatul NU mai folosește modulul ăsta de pe 12 aug 2026 (dictarea în română
 * era slabă, iar două microfoane lângă conversația vocală derutau) - rămâne viu
 * pentru compozitorul clasic (`composer.ts`, panoul configuratorului).
 */

interface RecognitionAlternative {
  transcript: string;
}
interface RecognitionResult {
  isFinal: boolean;
  0: RecognitionAlternative;
}
interface RecognitionEvent {
  resultIndex: number;
  results: { length: number; [index: number]: RecognitionResult };
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: RecognitionEvent) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  onaudiostart: (() => void) | null;
  onspeechstart: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

/** Un tag scurt („ro") derutează unele backend-uri: completăm regiunea uzuală. */
function fullLocale(lang: string): string {
  const known: Record<string, string> = { ro: "ro-RO", en: "en-US", de: "de-DE", fr: "fr-FR", es: "es-ES", it: "it-IT" };
  return lang.includes("-") ? lang : (known[lang.toLowerCase()] ?? lang);
}

function recognitionCtor(): (new () => Recognition) | null {
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as (new () => Recognition) | null;
}

export function dictationSupported(): boolean {
  return recognitionCtor() !== null && window.isSecureContext;
}

export interface DictationCallbacks {
  /** text = tot ce s-a dictat în sesiunea curentă (final + interimar) */
  onText: (text: string) => void;
  onEnd: () => void;
  onError: (error: string) => void;
  /** microfonul chiar captează sunet (feedback pentru admin) */
  onHearing?: () => void;
}

export interface Dictation {
  start(): void;
  stop(): void;
  readonly listening: boolean;
}

export function createDictation(lang: string, callbacks: DictationCallbacks): Dictation | null {
  const Ctor = recognitionCtor();
  if (!Ctor || !window.isSecureContext) return null;

  let active = false;
  const rec = new Ctor();
  rec.lang = fullLocale(lang);
  rec.continuous = true;
  rec.interimResults = true;

  // diagnosticabil din consolă: vedem exact unde se oprește lanțul
  // (permisiune → audio captat → vorbire detectată → rezultate)
  rec.onaudiostart = () => console.info("[ai-guidance] dictation: audio start");
  rec.onspeechstart = () => {
    console.info("[ai-guidance] dictation: speech detected");
    callbacks.onHearing?.();
  };
  rec.onresult = (e) => {
    // Transcriptul se RECONSTRUIEȘTE din toate rezultatele la fiecare eveniment,
    // niciodată acumulat peste evenimente. `e.results` conține oricum întreaga
    // sesiune, deci reconstrucția e corectă pe desktop; pe Chrome de Android e
    // singura interpretare corectă: acolo API-ul retrimite transcriptul cumulat
    // (tot ce s-a spus până atunci), re-marcat `isFinal` la fiecare eveniment.
    // Vechiul `finalText += chunk` lipea prefixele între ele și mesaje de forma
    // „AspettoAspetto ancoraAspetto ancora consegnare…" ajungeau trimise în chat
    // (văzut în producție, 1–6 aug 2026).
    let text = "";
    for (let i = 0; i < e.results.length; i++) text += e.results[i][0].transcript;
    console.info("[ai-guidance] dictation: result", JSON.stringify(text.slice(0, 80)));
    callbacks.onText(text.trim());
  };
  rec.onerror = (e) => {
    console.warn("[ai-guidance] dictation: error", e.error);
    active = false;
    callbacks.onError(e.error);
  };
  rec.onend = () => {
    console.info("[ai-guidance] dictation: end");
    active = false;
    callbacks.onEnd();
  };

  return {
    start() {
      if (active) return;
      active = true;
      rec.start();
    },
    stop() {
      if (!active) return;
      active = false;
      rec.stop();
    },
    get listening() {
      return active;
    },
  };
}
